import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  RssFeed as FeedIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Block as BlockIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@material-ui/icons'
import {
  GET_AI_COACHING_INTERVENTION,
  UPDATE_AI_COACHING_INTERVENTION,
  SEND_AI_COACHING_EMAIL,
  POST_AI_COACHING_TO_FEED,
  DECLINE_AI_COACHING_INTERVENTION,
  CLAIM_AI_COACHING_INTERVENTION,
} from '../../../../Apollo/Queries/AiCoachingInterventions'
import {
  InterventionHeader,
  MessagePreview,
  FeedbackSection,
  ActionButtons,
  UserInfo,
} from './CoachingInterventions.styled'

interface RouteParams {
  id: string
}

export const CoachingInterventionDetail: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const location = useLocation()
  const interventionId = parseInt(id, 10)

  // Get the tab from query params
  const searchParams = new URLSearchParams(location.search)
  const tabFromUrl = searchParams.get('tab')

  const [isEditing, setIsEditing] = useState(false)
  const [editedMessage, setEditedMessage] = useState('')
  const [coachFeedback, setCoachFeedback] = useState('')
  const [coachRating, setCoachRating] = useState<'GOOD' | 'BAD' | null>(null)
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const { data, loading, error, refetch } = useQuery(
    GET_AI_COACHING_INTERVENTION,
    {
      variables: { id: interventionId },
      skip: !interventionId,
    }
  )

  const [claimIntervention] = useMutation(CLAIM_AI_COACHING_INTERVENTION)
  const [updateIntervention] = useMutation(UPDATE_AI_COACHING_INTERVENTION)
  const [sendEmail] = useMutation(SEND_AI_COACHING_EMAIL)
  const [postToFeed] = useMutation(POST_AI_COACHING_TO_FEED)
  const [declineIntervention] = useMutation(DECLINE_AI_COACHING_INTERVENTION)

  const intervention = data?.aiCoachingIntervention

  useEffect(() => {
    if (intervention) {
      setEditedMessage(intervention.editedMessage || intervention.message || '')
      setCoachFeedback(intervention.coachFeedback || '')
      setCoachRating(intervention.coachRating || null)
      setHasUnsavedChanges(false)
    }
  }, [intervention])

  // Track changes to feedback and rating
  useEffect(() => {
    if (intervention) {
      const feedbackChanged =
        coachFeedback !== (intervention.coachFeedback || '')
      const ratingChanged = coachRating !== (intervention.coachRating || null)
      const messageChanged =
        isEditing &&
        editedMessage !==
          (intervention.editedMessage || intervention.message || '')

      setHasUnsavedChanges(feedbackChanged || ratingChanged || messageChanged)
    }
  }, [coachFeedback, coachRating, editedMessage, isEditing, intervention])

  const handleBack = () => {
    // Navigate back to the tab they came from
    const tabParam = tabFromUrl ? `?tab=${tabFromUrl}` : ''
    history.push(`/internal/self-serve/coaching-interventions${tabParam}`)
  }

  const handleClaim = async () => {
    try {
      const result = await claimIntervention({
        variables: { interventionId },
      })

      if (result.data?.claimAiCoachingIntervention?.success) {
        setSnackbar({
          open: true,
          message: 'Intervention claimed successfully!',
          severity: 'success',
        })
        refetch()
      } else {
        setSnackbar({
          open: true,
          message:
            result.data?.claimAiCoachingIntervention?.message ||
            'Failed to claim intervention',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  const handleSaveChanges = async () => {
    try {
      const result = await updateIntervention({
        variables: {
          interventionId,
          editedMessage,
          coachFeedback,
          coachRating,
        },
      })

      if (result.data?.updateAiCoachingIntervention?.success) {
        setSnackbar({
          open: true,
          message: 'Changes saved successfully!',
          severity: 'success',
        })
        setIsEditing(false)
        setHasUnsavedChanges(false)
        refetch()
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to save changes',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  const handleSendEmail = async () => {
    try {
      const result = await sendEmail({
        variables: { interventionId },
      })

      if (result.data?.sendAiCoachingEmail?.success) {
        setSnackbar({
          open: true,
          message: 'Email sent successfully!',
          severity: 'success',
        })
        refetch()
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to send email',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  const handlePostToFeed = async () => {
    try {
      const result = await postToFeed({
        variables: { interventionId },
      })

      if (result.data?.postAiCoachingToFeed?.success) {
        setSnackbar({
          open: true,
          message: 'Posted to feed successfully!',
          severity: 'success',
        })
        refetch()
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to post to feed',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  const handleDecline = async () => {
    try {
      const result = await declineIntervention({
        variables: { interventionId, reason: declineReason },
      })

      if (result.data?.declineAiCoachingIntervention?.success) {
        setSnackbar({
          open: true,
          message: 'Intervention declined',
          severity: 'success',
        })
        setDeclineDialogOpen(false)
        refetch()
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to decline intervention',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !intervention) {
    return (
      <Box p={4}>
        <Alert severity='error'>
          {error ? error.message : 'Intervention not found'}
        </Alert>
        <Button
          variant='outlined'
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Box>
    )
  }

  const canTakeAction =
    intervention.claimedBy &&
    intervention.status !== 'DECLINED' &&
    intervention.status !== 'POSTED_AND_EMAILED'

  const getStatusColor = (status: string) => {
    if (status === 'NEW') return 'primary'
    if (status === 'DECLINED') return 'error'
    return 'success'
  }

  return (
    <Box p={3}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
        Back to Interventions
      </Button>

      <InterventionHeader>
        <Box>
          <Typography variant='h4' gutterBottom>
            Intervention #{intervention.id}
          </Typography>
          <Box display='flex' gap={1} alignItems='center'>
            <Chip
              label={intervention.status}
              color={getStatusColor(intervention.status)}
            />
            <Typography variant='body2' color='textSecondary'>
              {intervention.identifier.replace(/([A-Z])/g, ' $1').trim()} •
              Created {new Date(intervention.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
        {intervention.status === 'NEW' && !intervention.claimedBy && (
          <Button variant='contained' color='primary' onClick={handleClaim}>
            Claim This Intervention
          </Button>
        )}
      </InterventionHeader>

      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            User Information
          </Typography>
          <UserInfo>
            <Typography>
              <strong>Name:</strong> {intervention.user?.firstName}{' '}
              {intervention.user?.lastName}
            </Typography>
            <Typography>
              <strong>Email:</strong> {intervention.user?.email}
            </Typography>
          </UserInfo>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
          >
            <Typography variant='h6'>Message</Typography>
            {canTakeAction && (
              <Button
                startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel' : 'Edit Message'}
              </Button>
            )}
          </Box>

          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={8}
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
              variant='outlined'
            />
          ) : (
            <MessagePreview>
              <Typography style={{ whiteSpace: 'pre-wrap' }}>
                {intervention.editedMessage || intervention.message}
              </Typography>
            </MessagePreview>
          )}
        </CardContent>
      </Card>

      {canTakeAction && (
        <FeedbackSection>
          <Typography variant='h6' gutterBottom>
            Coach Feedback
          </Typography>

          <Box mb={2}>
            <Typography variant='subtitle2' gutterBottom>
              Rate this message:
            </Typography>
            <ToggleButtonGroup
              value={coachRating}
              exclusive
              onChange={(_, value) => setCoachRating(value)}
            >
              <ToggleButton value='GOOD'>
                <ThumbUpIcon style={{ marginRight: 8 }} /> Good
              </ToggleButton>
              <ToggleButton value='BAD'>
                <ThumbDownIcon style={{ marginRight: 8 }} /> Needs Improvement
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={3}
            label='Feedback Notes (optional)'
            value={coachFeedback}
            onChange={(e) => setCoachFeedback(e.target.value)}
            variant='outlined'
          />

          {hasUnsavedChanges && (
            <Button
              variant='contained'
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
              sx={{ mt: 2 }}
            >
              Save Changes
            </Button>
          )}
        </FeedbackSection>
      )}

      {canTakeAction && (
        <ActionButtons>
          <Button
            variant='contained'
            color='primary'
            startIcon={<EmailIcon />}
            onClick={handleSendEmail}
            disabled={
              !!intervention.emailedAt || intervention.status === 'DECLINED'
            }
          >
            Send Email
          </Button>
          <Button
            variant='contained'
            color='success'
            startIcon={<FeedIcon />}
            onClick={handlePostToFeed}
            disabled={
              !!intervention.postedToFeedAt ||
              intervention.status === 'DECLINED'
            }
          >
            Post to Feed
          </Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<BlockIcon />}
            onClick={() => setDeclineDialogOpen(true)}
            disabled={
              intervention.status === 'DECLINED' ||
              intervention.status === 'POSTED_AND_EMAILED'
            }
          >
            Decline
          </Button>
        </ActionButtons>
      )}

      {intervention.status !== 'NEW' && (
        <Alert severity='info' sx={{ mt: 2 }}>
          This intervention has already been processed.
          {intervention.emailedAt && (
            <Typography variant='body2'>
              Emailed on {new Date(intervention.emailedAt).toLocaleString()}
            </Typography>
          )}
          {intervention.postedToFeedAt && (
            <Typography variant='body2'>
              Posted to feed on{' '}
              {new Date(intervention.postedToFeedAt).toLocaleString()}
            </Typography>
          )}
          {intervention.declinedAt && (
            <Typography variant='body2'>
              Declined on {new Date(intervention.declinedAt).toLocaleString()}
              {intervention.declineReason && `: ${intervention.declineReason}`}
            </Typography>
          )}
        </Alert>
      )}

      <Dialog
        open={declineDialogOpen}
        onClose={() => setDeclineDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Decline Intervention</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Please provide a reason for declining this intervention.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Reason for declining'
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            variant='outlined'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDecline}
            color='error'
            variant='contained'
            disabled={!declineReason.trim()}
          >
            Decline Intervention
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
